import React, { useState } from "react";
import { api, fileUrl } from "../lib/api";

const TYPE_CONFIG = {
  image: {
    accept: "image/jpeg,image/png,image/webp",
    label: "Formats acceptés : JPG, PNG, WEBP",
    extensions: /\.(jpg|jpeg|png|webp)$/i,
  },
  video: {
    accept: "video/mp4,video/webm,video/quicktime",
    label: "Formats acceptés : MP4, WEBM, MOV",
    extensions: /\.(mp4|webm|mov)$/i,
  },
  pdf: {
    accept: "application/pdf",
    label: "Format accepté : PDF",
    extensions: /\.(pdf)$/i,
  },
  all: {
    accept:
      "image/jpeg,image/png,image/webp,application/pdf,video/mp4,video/webm,video/quicktime",
    label: "Formats acceptés : JPG, PNG, WEBP, PDF, MP4, WEBM, MOV",
    extensions: /\.(jpg|jpeg|png|webp|pdf|mp4|webm|mov)$/i,
  },
};

// Tous les nouveaux fichiers passent par /files/upload.
// Le backend envoie ensuite vers Cloudinary et retourne une URL HTTPS durable.
// Les anciennes valeurs data:image/... restent affichables pour compatibilité avec les contenus déjà enregistrés.
export default function ImageUpload({
  value,
  onChange,
  label = "Fichier",
  type = "all",
  storage = "upload", // conservé pour compatibilité, mais les nouveaux uploads passent tous par Cloudinary
  uploadContext = "general",
  seoSlug = "",
  seoTitle = "",
  showCurrentUrl = false,
  currentUrlLabel = "Image actuelle",
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const config = TYPE_CONFIG[type] || TYPE_CONFIG.all;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");

    // 🔒 Sécurité FRONT (avant appel API)
    if (!config.extensions.test(file.name)) {
      setError(`Fichier refusé. ${config.label}`);
      e.target.value = "";
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("context", uploadContext);
      formData.append("seo_slug", seoSlug || "");
      formData.append("seo_title", seoTitle || "");

      const res = await api.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.url) {
        onChange(res.data.url);
      } else {
        setError("Upload réussi, mais aucune URL retournée.");
      }
    } catch (err) {
      const backendMessage = err?.response?.data?.detail;
      const message = err?.message;

      setError(
        backendMessage ||
          message ||
          `Fichier refusé. ${config.label}`
      );
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const displayUrl = value?.startsWith?.("data:") ? value : fileUrl(value);

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-semibold text-gray-700">
          {label}
        </label>
      )}

      {/* Aperçu */}
      {value && (
        <div className={`rounded-xl border p-3 ${showCurrentUrl ? "border-emerald-100 bg-emerald-50/60" : "border-gray-200 bg-gray-50"}`}>
          <div className={showCurrentUrl ? "flex items-center gap-3" : ""}>
            {TYPE_CONFIG.video.extensions.test(value) ? (
              <video
                src={displayUrl}
                controls
                className="w-full max-h-64 rounded-lg bg-black object-contain"
              />
            ) : TYPE_CONFIG.pdf.extensions.test(value) ? (
              <a
                href={displayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-green underline"
              >
                Voir le PDF actuel
              </a>
            ) : (
              <img
                src={displayUrl}
                alt="Aperçu"
                className={showCurrentUrl ? "w-28 h-20 object-contain rounded-xl border border-white shadow-sm" : "max-h-64 rounded-lg object-contain"}
              />
            )}
            {showCurrentUrl && (
              <p className="text-xs text-emerald-700 break-all">
                {currentUrlLabel} : {value}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Input */}
      <input
        type="file"
        accept={config.accept}
        onChange={handleUpload}
        disabled={uploading}
        className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-full file:border-0 file:bg-brand-green file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-emerald-600 disabled:opacity-50"
      />

      {/* Indication formats */}
      <p className="text-xs text-gray-500">
        {config.label} · Stockage Cloudinary durable après déploiement
      </p>

      {uploading && (
        <p className="text-sm text-gray-500">Upload en cours...</p>
      )}

      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
